/**
 * Regiones de interés rectangulares en coordenadas normalizadas de imagen.
 *
 * Convención de bordes: un punto pertenece al ROI si `x ∈ [rx, rx+w]` y `y ∈ [ry, ry+h]`
 * (ambos bordes incluidos). Por tanto un punto exactamente en el borde común de dos ROIs
 * adyacentes cuenta en ambos.
 */
export interface RectRoi {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function pointInRect(px: number, py: number, roi: RectRoi): boolean {
  return px >= roi.x && px <= roi.x + roi.width && py >= roi.y && py <= roi.y + roi.height;
}

/** Fracción de puntos (en imagen) dentro del ROI. `null` si no hay puntos. */
export function roiFraction(points: ReadonlyArray<{ x: number; y: number }>, roi: RectRoi): number | null {
  if (points.length === 0) return null;
  let inside = 0;
  for (const p of points) if (pointInRect(p.x, p.y, roi)) inside++;
  return inside / points.length;
}

export function mean(values: ReadonlyArray<number>): number | null {
  if (values.length === 0) return null;
  let s = 0;
  for (const v of values) s += v;
  return s / values.length;
}

/** Desviación estándar muestral (n−1). `null` con menos de 2 valores. */
export function sampleSd(values: ReadonlyArray<number>): number | null {
  if (values.length < 2) return null;
  const m = mean(values)!;
  let s = 0;
  for (const v of values) s += (v - m) ** 2;
  return Math.sqrt(s / (values.length - 1));
}

export function median(values: ReadonlyArray<number>): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}
